/**
 * Hooks for fetching schedule data.
 * Sprint 8, SDD §7.2 — Schedule UI & Calendar.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// ── Types ────────────────────────────────────────────────────────────────

export interface ScheduleDayEntry {
  id?: number;
  date: string;
  battalion: {
    id: number;
    name: string;
    number: number;
  };
  shift_start: string;
  shift_end: string;
  is_weekend: boolean;
  is_holiday: boolean;
  is_override: boolean;
  notes: string | null;
}

interface TodayResponse {
  date: string;
  on_duty: ScheduleDayEntry | null;
  message?: string;
}

interface WeekResponse {
  start_date: string;
  end_date: string;
  days: ScheduleDayEntry[];
}

interface MonthResponse {
  year: number;
  month: number;
  start_date: string;
  end_date: string;
  days: ScheduleDayEntry[];
}

// ── useScheduleToday ─────────────────────────────────────────────────────

export interface UseScheduleTodayReturn {
  duty: ScheduleDayEntry | null;
  loading: boolean;
  error: string | null;
}

export function useScheduleToday(): UseScheduleTodayReturn {
  const [duty, setDuty] = useState<ScheduleDayEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchToday() {
      try {
        const data = await apiFetch<TodayResponse>('/api/schedule/today');
        if (!cancelled) {
          setDuty(data.on_duty);
        }
      } catch {
        if (!cancelled) {
          setError("Failed to load today's schedule");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchToday();
    return () => {
      cancelled = true;
    };
  }, []);

  return { duty, loading, error };
}

// ── useScheduleWeek ──────────────────────────────────────────────────────

export interface UseScheduleWeekReturn {
  days: ScheduleDayEntry[];
  startDate: string | null;
  endDate: string | null;
  loading: boolean;
  error: string | null;
}

export function useScheduleWeek(): UseScheduleWeekReturn {
  const [days, setDays] = useState<ScheduleDayEntry[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchWeek() {
      try {
        const data = await apiFetch<WeekResponse>('/api/schedule/week');
        if (!cancelled) {
          setDays(data.days);
          setStartDate(data.start_date);
          setEndDate(data.end_date);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load week schedule');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchWeek();
    return () => {
      cancelled = true;
    };
  }, []);

  return { days, startDate, endDate, loading, error };
}

// ── useScheduleMonth ─────────────────────────────────────────────────────

export interface UseScheduleMonthReturn {
  days: ScheduleDayEntry[];
  year: number;
  month: number;
  loading: boolean;
  error: string | null;
  goToMonth: (year: number, month: number) => void;
}

export function useScheduleMonth(initialYear?: number, initialMonth?: number): UseScheduleMonthReturn {
  const now = new Date();
  const [year, setYear] = useState(initialYear ?? now.getFullYear());
  const [month, setMonth] = useState(initialMonth ?? now.getMonth() + 1);
  const [days, setDays] = useState<ScheduleDayEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const goToMonth = useCallback((y: number, m: number) => {
    setYear(y);
    setMonth(m);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    async function fetchMonth() {
      try {
        const data = await apiFetch<MonthResponse>(`/api/schedule/month?year=${year}&month=${month}`);
        if (!cancelled) {
          setDays(data.days);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load month schedule');
          setDays([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchMonth();
    return () => {
      cancelled = true;
    };
  }, [year, month]);

  return { days, year, month, loading, error, goToMonth };
}
